/**
 * Adapted from PlayCanvas Engine's gsplat-director.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import type * as THREE from 'three'
import type { Layer } from '@/plugins/gsplat/lod/Layer'
import type { ViewId } from '@/shared/types/view'
import {
  combineGSplatRenderPasses,
  GSPLAT_RENDER_MODE_FORWARD,
  GSPLAT_RENDER_MODE_NONE,
  GSPLAT_RENDER_MODE_SHADOW,
  type GSplatRenderPassMask,
} from './GSplatPipelineTypes'

export interface GSplatManagerConfigurationTransaction {
  /** stage 成功后 commit 必须为无失败的引用/状态交换。 */
  commit(): void
  /** finalize 前即使已经 commit，也必须恢复 stage 前的配置。 */
  rollback(): void
}

/** Director 对 Manager 的窄上游协议；不创建第二套 runtime。 */
export interface GSplatDirectorManager<TPlacement extends object = object> {
  /** Unified Manager 用于将 streaming/sort activity 定向到所属 View；兼容 Manager 可省略。 */
  readonly viewId?: ViewId
  readonly material: unknown
  readonly bufferCopyUploaded: number
  readonly bufferCopyTotal: number
  readonly hasPendingSort: boolean
  setRenderMode(renderMode: GSplatRenderPassMask): void
  reconcile(placements: readonly TPlacement[]): void
  stageConfiguration(
    renderMode: GSplatRenderPassMask,
    placements: readonly TPlacement[]
  ): GSplatManagerConfigurationTransaction
  updateStreaming(logicalFrameToken: number): boolean
  update(): number
  prepareForPicking(camera: THREE.Camera, width: number, height: number): unknown | null
  updateShadows(): void
  destroy(): void
}

export interface GSplatDirectorManagerFactory<
  TPlacement extends object = object,
  TDrawObject extends object = object,
> {
  create(input: {
    readonly viewId: ViewId
    readonly camera: THREE.Camera
    readonly cameraRevision: number
    readonly layer: Layer<TPlacement, TDrawObject>
    readonly shadowOnly: boolean
  }): GSplatDirectorManager<TPlacement>
}

export interface GSplatDirectorView<
  TPlacement extends object = object,
  TDrawObject extends object = object,
> {
  readonly viewId: ViewId
  readonly camera: THREE.Camera
  readonly cameraRevision: number
  readonly layers: readonly Layer<TPlacement, TDrawObject>[]
}

export interface GSplatDirectorCallbacks<
  TPlacement extends object = object,
  TDrawObject extends object = object,
> {
  onMaterialCreated?(
    material: unknown,
    viewId: ViewId,
    camera: THREE.Camera,
    layer: Layer<TPlacement, TDrawObject>,
    shadowOnly: boolean
  ): void
  onStats?(stats: { readonly gsplatCount: number; readonly bufferCopyPercent: number }): void
  onFrameEnd?(): void
  applyParams?(): void
  processResourceCleanup?(): void
  clearParamsDirty?(): void
  requestFrame?(viewIds?: readonly ViewId[]): void
}

interface NewManager<TPlacement extends object> {
  readonly manager: GSplatDirectorManager<TPlacement>
  readonly shadowOnly: boolean
  readonly placements: readonly TPlacement[]
}

interface LayerConfigurationPlan<TPlacement extends object> {
  readonly newManagers: readonly NewManager<TPlacement>[]
  prepare(): void
  commit(): void
  finalize(): void
  rollback(): void
}

function setsEqual<T>(left: ReadonlySet<T>, right: ReadonlySet<T>): boolean {
  if (left.size !== right.size) return false
  for (const value of left) if (!right.has(value)) return false
  return true
}

/** Per `(ViewId, camera revision, Layer)` 的 main/shadow Manager owner。 */
export class GSplatLayerData<
  TPlacement extends object = object,
  TDrawObject extends object = object,
> {
  gsplatManager: GSplatDirectorManager<TPlacement> | null = null
  gsplatManagerShadow: GSplatDirectorManager<TPlacement> | null = null

  constructor(
    private readonly managerFactory: GSplatDirectorManagerFactory<TPlacement, TDrawObject>,
    private readonly callbacks: GSplatDirectorCallbacks<TPlacement, TDrawObject>,
    readonly viewId: ViewId,
    readonly camera: THREE.Camera,
    readonly cameraRevision: number,
    readonly layer: Layer<TPlacement, TDrawObject>
  ) {}

  planConfiguration(): LayerConfigurationPlan<TPlacement> {
    const hasNormalPlacements = this.layer.gsplatPlacements.length > 0
    const hasShadowCasters = this.layer.gsplatShadowCasters.length > 0
    const useSharedManager =
      hasNormalPlacements &&
      setsEqual(this.layer.gsplatPlacementsSet, this.layer.gsplatShadowCastersSet)
    const desiredMainMode = useSharedManager
      ? combineGSplatRenderPasses(GSPLAT_RENDER_MODE_FORWARD, GSPLAT_RENDER_MODE_SHADOW)
      : hasNormalPlacements
        ? GSPLAT_RENDER_MODE_FORWARD
        : GSPLAT_RENDER_MODE_NONE
    const desiredShadowMode = useSharedManager
      ? GSPLAT_RENDER_MODE_NONE
      : hasShadowCasters
        ? GSPLAT_RENDER_MODE_SHADOW
        : GSPLAT_RENDER_MODE_NONE

    const oldMain = this.gsplatManager
    const oldShadow = this.gsplatManagerShadow
    const newManagers: NewManager<TPlacement>[] = []
    const transactions: GSplatManagerConfigurationTransaction[] = []
    let nextMain: GSplatDirectorManager<TPlacement> | null = null
    let nextShadow: GSplatDirectorManager<TPlacement> | null = null
    let committed = false
    let finalized = false
    let rolledBack = false

    const stageManager = (
      current: GSplatDirectorManager<TPlacement> | null,
      renderMode: GSplatRenderPassMask,
      placements: readonly TPlacement[],
      shadowOnly: boolean
    ): GSplatDirectorManager<TPlacement> | null => {
      if (renderMode === GSPLAT_RENDER_MODE_NONE) return null
      if (current) {
        transactions.push(current.stageConfiguration(renderMode, placements))
        return current
      }

      const manager = this.managerFactory.create({
        viewId: this.viewId,
        camera: this.camera,
        cameraRevision: this.cameraRevision,
        layer: this.layer,
        shadowOnly,
      })
      try {
        manager.setRenderMode(renderMode)
      } catch (error) {
        manager.destroy()
        throw error
      }
      newManagers.push({ manager, shadowOnly, placements })
      return manager
    }

    try {
      nextMain = stageManager(oldMain, desiredMainMode, this.layer.gsplatPlacements, false)
      nextShadow = stageManager(oldShadow, desiredShadowMode, this.layer.gsplatShadowCasters, true)
    } catch (error) {
      for (let index = transactions.length - 1; index >= 0; index--) {
        transactions[index].rollback()
      }
      for (let index = newManagers.length - 1; index >= 0; index--) {
        newManagers[index].manager.destroy()
      }
      throw error
    }

    return {
      newManagers,
      prepare: () => {
        for (const staged of newManagers) {
          this.callbacks.onMaterialCreated?.(
            staged.manager.material,
            this.viewId,
            this.camera,
            this.layer,
            staged.shadowOnly
          )
          staged.manager.reconcile(staged.placements)
        }
      },
      commit: () => {
        if (committed || rolledBack) return
        for (const transaction of transactions) transaction.commit()
        this.gsplatManager = nextMain
        this.gsplatManagerShadow = nextShadow
        committed = true
      },
      finalize: () => {
        if (!committed || finalized || rolledBack) return
        if (oldMain && oldMain !== nextMain && oldMain !== nextShadow) oldMain.destroy()
        if (oldShadow && oldShadow !== nextMain && oldShadow !== nextShadow) oldShadow.destroy()
        finalized = true
      },
      rollback: () => {
        if (finalized || rolledBack) return
        for (let index = transactions.length - 1; index >= 0; index--) {
          transactions[index].rollback()
        }
        this.gsplatManager = oldMain
        this.gsplatManagerShadow = oldShadow
        for (let index = newManagers.length - 1; index >= 0; index--) {
          newManagers[index].manager.destroy()
        }
        rolledBack = true
      },
    }
  }

  destroy(): void {
    this.gsplatManager?.destroy()
    this.gsplatManager = null
    this.gsplatManagerShadow?.destroy()
    this.gsplatManagerShadow = null
  }
}

/** Per View 的 Camera identity/revision 与 LayerData map。 */
export class GSplatCameraData<
  TPlacement extends object = object,
  TDrawObject extends object = object,
> {
  readonly layersMap = new Map<
    Layer<TPlacement, TDrawObject>,
    GSplatLayerData<TPlacement, TDrawObject>
  >()

  constructor(
    readonly viewId: ViewId,
    readonly camera: THREE.Camera,
    readonly cameraRevision: number
  ) {}

  destroy(): void {
    for (const layerData of this.layersMap.values()) layerData.destroy()
    this.layersMap.clear()
  }
}

interface CameraTopologyPlan<TPlacement extends object, TDrawObject extends object> {
  readonly view: GSplatDirectorView<TPlacement, TDrawObject>
  readonly oldData: GSplatCameraData<TPlacement, TDrawObject> | null
  readonly nextData: GSplatCameraData<TPlacement, TDrawObject>
  readonly layerPlans: readonly {
    readonly layerData: GSplatLayerData<TPlacement, TDrawObject>
    readonly isNew: boolean
    readonly plan: LayerConfigurationPlan<TPlacement>
  }[]
  readonly layersToRemove: readonly {
    readonly layer: Layer<TPlacement, TDrawObject>
    readonly layerData: GSplatLayerData<TPlacement, TDrawObject>
  }[]
}

/** View-aware 的 PlayCanvas Director 拓扑与 update 顺序实现。 */
export class GSplatDirector<
  TPlacement extends object = object,
  TDrawObject extends object = object,
> {
  readonly camerasMap = new Map<ViewId, GSplatCameraData<TPlacement, TDrawObject>>()

  private readonly managerFactory: GSplatDirectorManagerFactory<TPlacement, TDrawObject>
  private readonly callbacks: GSplatDirectorCallbacks<TPlacement, TDrawObject>
  private readonly managerStreamTokens = new WeakMap<GSplatDirectorManager<TPlacement>, number>()
  private lastStreamingPreludeToken: number | null = null
  private lastParamsClearToken: number | null = null
  private lastFrameRequestToken: number | null = null
  private currentStreamingToken: number | null = null
  private destroyed = false

  constructor(
    managerFactory: GSplatDirectorManagerFactory<TPlacement, TDrawObject>,
    callbacks: GSplatDirectorCallbacks<TPlacement, TDrawObject> = {}
  ) {
    this.managerFactory = managerFactory
    this.callbacks = callbacks
  }

  update(
    views: readonly GSplatDirectorView<TPlacement, TDrawObject>[],
    logicalFrameToken: number | null = this.currentStreamingToken,
    targetViewIds?: ReadonlySet<ViewId>
  ): void {
    this.assertActive()
    this.validateViews(views)
    const topologyPlans: CameraTopologyPlan<TPlacement, TDrawObject>[] = []

    try {
      for (const view of views) topologyPlans.push(this.planCameraTopology(view))
    } catch (error) {
      this.rollbackTopology(topologyPlans)
      throw error
    }

    const desiredViews = new Set(views.map((view) => view.viewId))
    const removedCameraData: GSplatCameraData<TPlacement, TDrawObject>[] = []
    for (const [viewId, cameraData] of this.camerasMap) {
      if (!desiredViews.has(viewId)) removedCameraData.push(cameraData)
    }

    // 与上游 update 顺序一致：先从可达 topology 脱离 remove，再准备 dirty/new reconcile。
    for (const topology of topologyPlans) {
      for (const removed of topology.layersToRemove) {
        topology.nextData.layersMap.delete(removed.layer)
      }
      if (topology.oldData && topology.oldData !== topology.nextData) {
        this.camerasMap.delete(topology.view.viewId)
      }
    }
    for (const cameraData of removedCameraData) this.camerasMap.delete(cameraData.viewId)

    try {
      for (const topology of topologyPlans) {
        for (const layerPlan of topology.layerPlans) layerPlan.plan.prepare()
      }
    } catch (error) {
      this.rollbackTopology(topologyPlans)
      for (const cameraData of removedCameraData) {
        this.camerasMap.set(cameraData.viewId, cameraData)
      }
      throw error
    }

    try {
      for (const topology of topologyPlans) {
        for (const layerPlan of topology.layerPlans) {
          layerPlan.plan.commit()
          if (layerPlan.isNew) {
            topology.nextData.layersMap.set(layerPlan.layerData.layer, layerPlan.layerData)
          }
        }
        this.camerasMap.set(topology.view.viewId, topology.nextData)
      }

      if (logicalFrameToken !== null) this.runStreaming(logicalFrameToken, false)

      let gsplatCount = 0
      let bufferCopyUploaded = 0
      let bufferCopyTotal = 0
      for (const view of views) {
        if (targetViewIds && !targetViewIds.has(view.viewId)) continue
        const cameraData = this.camerasMap.get(view.viewId)
        if (!cameraData) continue
        for (const layer of view.layers) {
          if (
            !layer.enabled ||
            (layer.gsplatPlacements.length === 0 && layer.gsplatShadowCasters.length === 0)
          ) {
            continue
          }
          const layerData = cameraData.layersMap.get(layer)
          if (!layerData) continue
          const managers = [layerData.gsplatManager, layerData.gsplatManagerShadow]
          for (const manager of managers) {
            if (!manager) continue
            gsplatCount += manager.update()
            bufferCopyUploaded += manager.bufferCopyUploaded
            bufferCopyTotal += manager.bufferCopyTotal
          }
        }
      }

      this.callbacks.onStats?.({
        gsplatCount,
        bufferCopyPercent: bufferCopyTotal > 0 ? (bufferCopyUploaded / bufferCopyTotal) * 100 : 0,
      })
      this.callbacks.onFrameEnd?.()
    } catch (error) {
      this.rollbackTopology(topologyPlans)
      for (const cameraData of removedCameraData) {
        this.camerasMap.set(cameraData.viewId, cameraData)
      }
      throw error
    }

    for (const topology of topologyPlans) {
      for (const layerPlan of topology.layerPlans) layerPlan.plan.finalize()
      for (const removed of topology.layersToRemove) removed.layerData.destroy()
      if (topology.oldData && topology.oldData !== topology.nextData) topology.oldData.destroy()
    }
    for (const cameraData of removedCameraData) {
      this.camerasMap.delete(cameraData.viewId)
      cameraData.destroy()
    }

    const clearedLayers = new Set<Layer<TPlacement, TDrawObject>>()
    for (const view of views) {
      for (const layer of view.layers) {
        if (clearedLayers.has(layer)) continue
        layer.gsplatPlacementsDirty = false
        clearedLayers.add(layer)
      }
    }
  }

  getLayerData(
    viewId: ViewId,
    layer: Layer<TPlacement, TDrawObject>
  ): GSplatLayerData<TPlacement, TDrawObject> | null {
    return this.camerasMap.get(viewId)?.layersMap.get(layer) ?? null
  }

  /** System-wide 配置事务与 per-View Prepare 精确失效使用的稳定 Manager 快照。 */
  getManagers(viewId?: ViewId): readonly GSplatDirectorManager<TPlacement>[] {
    return Object.freeze(this.collectManagers(viewId))
  }

  updateStreaming(logicalFrameToken: number): boolean {
    this.assertActive()
    return this.runStreaming(logicalFrameToken, true)
  }

  prepareForPicking(
    viewId: ViewId,
    layer: Layer<TPlacement, TDrawObject>,
    width: number,
    height: number
  ): unknown | null {
    this.assertActive()
    if (!Number.isSafeInteger(width) || !Number.isSafeInteger(height) || width < 1 || height < 1) {
      throw new Error(`非法 GSplat pick target size: ${width}x${height}`)
    }
    const cameraData = this.camerasMap.get(viewId)
    if (!cameraData) return null
    const manager = cameraData.layersMap.get(layer)?.gsplatManager
    return manager?.prepareForPicking(cameraData.camera, width, height) ?? null
  }

  updateShadows(): void {
    this.assertActive()
    for (const manager of this.collectManagers()) manager.updateShadows()
  }

  destroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    for (const cameraData of this.camerasMap.values()) cameraData.destroy()
    this.camerasMap.clear()
    this.currentStreamingToken = null
  }

  private planCameraTopology(
    view: GSplatDirectorView<TPlacement, TDrawObject>
  ): CameraTopologyPlan<TPlacement, TDrawObject> {
    const oldData = this.camerasMap.get(view.viewId) ?? null
    const canReuse =
      oldData?.camera === view.camera && oldData.cameraRevision === view.cameraRevision
    const nextData = canReuse
      ? oldData
      : new GSplatCameraData<TPlacement, TDrawObject>(view.viewId, view.camera, view.cameraRevision)
    const desiredLayers = new Set<Layer<TPlacement, TDrawObject>>()
    const layerPlans: CameraTopologyPlan<TPlacement, TDrawObject>['layerPlans'][number][] = []

    try {
      for (const layer of view.layers) {
        if (!layer.enabled) continue
        const hasPlacements =
          layer.gsplatPlacements.length > 0 || layer.gsplatShadowCasters.length > 0
        if (!hasPlacements) continue
        desiredLayers.add(layer)
        let layerData = nextData.layersMap.get(layer)
        const isNew = !layerData
        layerData ??= new GSplatLayerData(
          this.managerFactory,
          this.callbacks,
          view.viewId,
          view.camera,
          view.cameraRevision,
          layer
        )
        if (isNew || layer.gsplatPlacementsDirty) {
          layerPlans.push({ layerData, isNew, plan: layerData.planConfiguration() })
        }
      }
    } catch (error) {
      for (let index = layerPlans.length - 1; index >= 0; index--) {
        layerPlans[index].plan.rollback()
      }
      throw error
    }

    const layersToRemove: CameraTopologyPlan<TPlacement, TDrawObject>['layersToRemove'][number][] =
      []
    for (const [layer, layerData] of nextData.layersMap) {
      if (!desiredLayers.has(layer)) layersToRemove.push({ layer, layerData })
    }

    return { view, oldData, nextData, layerPlans, layersToRemove }
  }

  private rollbackTopology(
    topologyPlans: readonly CameraTopologyPlan<TPlacement, TDrawObject>[]
  ): void {
    for (let viewIndex = topologyPlans.length - 1; viewIndex >= 0; viewIndex--) {
      const topology = topologyPlans[viewIndex]
      for (let index = topology.layerPlans.length - 1; index >= 0; index--) {
        const layerPlan = topology.layerPlans[index]
        layerPlan.plan.rollback()
        if (layerPlan.isNew) topology.nextData.layersMap.delete(layerPlan.layerData.layer)
      }
      for (const removed of topology.layersToRemove) {
        topology.nextData.layersMap.set(removed.layer, removed.layerData)
      }
      if (topology.oldData) this.camerasMap.set(topology.view.viewId, topology.oldData)
      else if (this.camerasMap.get(topology.view.viewId) === topology.nextData) {
        this.camerasMap.delete(topology.view.viewId)
      }
    }
  }

  private runStreaming(logicalFrameToken: number, requestFrame: boolean): boolean {
    this.validateLogicalFrameToken(logicalFrameToken)
    this.ensureStreamingPrelude(logicalFrameToken)
    this.currentStreamingToken = logicalFrameToken

    const managers = this.collectManagers()
    let needRender = false
    const activeViewIds = new Set<ViewId>()
    for (const manager of managers) {
      let managerNeedsRender = false
      if (this.managerStreamTokens.get(manager) !== logicalFrameToken) {
        managerNeedsRender = manager.updateStreaming(logicalFrameToken)
        this.managerStreamTokens.set(manager, logicalFrameToken)
      }
      managerNeedsRender = manager.hasPendingSort || managerNeedsRender
      needRender = managerNeedsRender || needRender
      if (managerNeedsRender && manager.viewId) activeViewIds.add(manager.viewId)
    }

    if (managers.length > 0 && this.lastParamsClearToken !== logicalFrameToken) {
      this.callbacks.clearParamsDirty?.()
      this.lastParamsClearToken = logicalFrameToken
    }
    if (requestFrame && needRender && this.lastFrameRequestToken !== logicalFrameToken) {
      this.callbacks.requestFrame?.(activeViewIds.size > 0 ? Array.from(activeViewIds) : undefined)
      this.lastFrameRequestToken = logicalFrameToken
    }
    return needRender
  }

  private ensureStreamingPrelude(logicalFrameToken: number): void {
    if (this.lastStreamingPreludeToken === logicalFrameToken) return
    if (
      this.lastStreamingPreludeToken !== null &&
      logicalFrameToken < this.lastStreamingPreludeToken
    ) {
      throw new Error(
        `GSplatDirector logical frame token 回退: ${logicalFrameToken} < ${this.lastStreamingPreludeToken}`
      )
    }
    this.callbacks.applyParams?.()
    this.callbacks.processResourceCleanup?.()
    this.lastStreamingPreludeToken = logicalFrameToken
  }

  private collectManagers(viewId?: ViewId): GSplatDirectorManager<TPlacement>[] {
    const result: GSplatDirectorManager<TPlacement>[] = []
    const seen = new Set<GSplatDirectorManager<TPlacement>>()
    const cameraDataValues = viewId
      ? [this.camerasMap.get(viewId)].filter(
          (value): value is GSplatCameraData<TPlacement, TDrawObject> => value !== undefined
        )
      : this.camerasMap.values()
    for (const cameraData of cameraDataValues) {
      for (const layerData of cameraData.layersMap.values()) {
        const candidates = [layerData.gsplatManager, layerData.gsplatManagerShadow]
        for (const manager of candidates) {
          if (!manager || seen.has(manager)) continue
          seen.add(manager)
          result.push(manager)
        }
      }
    }
    return result
  }

  private validateLogicalFrameToken(logicalFrameToken: number): void {
    if (!Number.isSafeInteger(logicalFrameToken) || logicalFrameToken < 0) {
      throw new Error(`非法 GSplat logical frame token: ${logicalFrameToken}`)
    }
  }

  private validateViews(views: readonly GSplatDirectorView<TPlacement, TDrawObject>[]): void {
    const viewIds = new Set<ViewId>()
    for (const view of views) {
      if (!view.viewId || viewIds.has(view.viewId)) {
        throw new Error(`GSplatDirector ViewId 缺失或重复: ${view.viewId}`)
      }
      viewIds.add(view.viewId)
      if (!Number.isSafeInteger(view.cameraRevision) || view.cameraRevision < 0) {
        throw new Error(`非法 GSplat cameraRevision: ${view.cameraRevision}`)
      }
      const layers = new Set<Layer<TPlacement, TDrawObject>>()
      for (const layer of view.layers) {
        if (layers.has(layer)) throw new Error(`View ${view.viewId} 包含重复 Layer identity`)
        layers.add(layer)
      }
    }
  }

  private assertActive(): void {
    if (this.destroyed) throw new Error('GSplatDirector 已销毁')
  }
}
